import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Rocket } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { SOURCES } from "@/lib/constants";

const DEFAULT_SOURCES = { localch: true, searchch: true, google: true, zefix: true, crawler: true, enrichment: false };

const keyMissing = (source, settings) =>
  source.needsKey && settings && !source.needsKey.split("|").some((k) => settings[`${k}_set`]);

const SourceCard = ({ source, checked, onChange, missing }) => (
  <label
    data-testid={`source-${source.key}-card`}
    className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors duration-150 ${
      checked ? "border-[#002FA7] bg-blue-50/50" : "border-border bg-white hover:bg-zinc-50"
    }`}
  >
    <Checkbox data-testid={`source-${source.key}-checkbox`} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" />
    <source.icon className={`mt-0.5 h-4 w-4 shrink-0 ${checked ? "text-[#002FA7]" : "text-zinc-400"}`} />
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
        {source.label}
        {missing && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">Clé requise</span>}
      </div>
      <div className="text-xs text-muted-foreground">{source.desc}</div>
    </div>
  </label>
);

export const SearchForm = ({ onCreated }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ keywords: "", location: "", radius_km: 10, max_results: "40", expand_area: true });
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [loading, setLoading] = useState(false);
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, radius_km: Number(form.radius_km) || 0, max_results: Number(form.max_results), sources };
      const { data } = await api.post("/jobs", payload);
      toast.success("Recherche globale lancée", { description: `${data.keywords} · ${data.location}` });
      onCreated(data.id);
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="search-form" className="fade-up rounded-lg border border-border bg-white p-6 shadow-sm">
      <div className="eyebrow mb-5">Paramètres de la recherche</div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
        <div className="space-y-1.5 md:col-span-3">
          <Label htmlFor="keywords">Activité / Mots-clés</Label>
          <Input id="keywords" data-testid="search-keywords-input" required minLength={2} maxLength={500} placeholder="ex. boulangerie, fiduciaire, garage" value={form.keywords} onChange={set("keywords")} />
          <p className="text-[11px] text-muted-foreground">Plusieurs activités ? Séparez-les par des virgules : chacune est recherchée séparément.</p>
        </div>
        <div className="space-y-1.5 md:col-span-3">
          <Label htmlFor="location">Localisation / Ville / Canton</Label>
          <Input id="location" data-testid="search-location-input" required minLength={2} placeholder="ex. Lausanne, Genève, VD" value={form.location} onChange={set("location")} />
        </div>
        <div className="space-y-1.5 md:col-span-3">
          <Label htmlFor="radius">Rayon (km)</Label>
          <Input id="radius" data-testid="search-radius-input" type="number" min={0} max={200} value={form.radius_km} onChange={set("radius_km")} />
          <p className="text-[11px] text-muted-foreground">0 = pas de filtre géographique. Appliqué aux fiches géolocalisées.</p>
        </div>
        <div className="space-y-1.5 md:col-span-3">
          <Label>Résultats max. par source et par activité</Label>
          <Select value={form.max_results} onValueChange={(v) => setForm((f) => ({ ...f, max_results: v }))}>
            <SelectTrigger data-testid="search-max-results-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["10", "20", "40", "100", "200", "500", "1000", "2500", "5000", "10000"].map((v) => (
                <SelectItem key={v} value={v} data-testid={`max-results-option-${v}`}>{Number(v).toLocaleString("fr-CH")} résultats</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {Number(form.max_results) > 200 && (
            <p data-testid="max-results-hint" className="text-[11px] text-muted-foreground">Au-delà de ~200 fiches par annuaire, la recherche s'étend automatiquement aux localités du rayon. Les grosses extractions tournent en arrière-plan (plusieurs heures possibles).</p>
          )}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm md:col-span-6">
          <Switch data-testid="expand-area-switch" checked={form.expand_area} onCheckedChange={(v) => setForm((f) => ({ ...f, expand_area: v }))} />
          Extension géographique automatique (toutes les localités dans le rayon, registre officiel swisstopo)
        </label>
      </div>
      <div className="eyebrow mb-3 mt-8">Sources & connecteurs</div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SOURCES.map((s) => (
          <SourceCard key={s.key} source={s} checked={sources[s.key]} missing={keyMissing(s, settings)} onChange={(v) => setSources((p) => ({ ...p, [s.key]: v }))} />
        ))}
      </div>
      <Button data-testid="launch-search-button" type="submit" disabled={loading} size="lg" className="mt-8 w-full transition-transform hover:-translate-y-0.5 sm:w-auto">
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Rocket className="mr-2 h-4 w-4" />}
        Lancer la recherche globale
      </Button>
    </form>
  );
};
